import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import CloseDialogButton from "../../../components/CloseDialogButton";
import EditButton from "../../../components/EditButton";
import ItemForm from "../../../components/forms/ItemForm";
import ItemLabel from "../../../components/ItemLabel";
import ItemSkeleton from "../../../components/ItemSkeletons";
import LazyImage from "../../../components/lazyImage";
import { useAuth } from "../../../contexts/AuthContext";
import { useLoading } from "../../../contexts/LoadingContext";
import useCrud from "../../../hooks/useCrud";
import { getItemById } from "../../../services/itemService";
import { isImageUrl } from "../../../utils/isImageUrl";
import ItemDetails from "../../Menu/ItemDetails";

const ItemTypes = { ITEM: "item" };

const ItemListAdmin = ({ items, selectedCategory, dimensions }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const { startLoading, stopLoading } = useLoading();
  const [isAnimating, setIsAnimating] = useState(false);
  const theme = useTheme();
  const { themeBg } = useAuth();
  const { reorderData } = useCrud("/items");
  const [itemOrder, setItemOrder] = useState(items);
  const [isItemFormOpen, setIsItemFormOpen] = useState(false);
  const [isReminderOpen, setIsReminderOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);

  const handleOpenItemForm = (itemId = null) => {
    setEditingItemId(itemId);
    setIsItemFormOpen(true);
  };

  const handleCloseItemForm = (active = true) => {
    if (active) {
      setIsItemFormOpen(false);
      setEditingItemId(null);
    } else {
      setIsReminderOpen(true);
    }
  };

  const handleCloseItemFormAfterReminderYes = () => {
    setIsReminderOpen(false);
    setIsItemFormOpen(false);
    setEditingItemId(null);
  };

  const handleCloseItemFormAfterReminderCancel = () => {
    setIsReminderOpen(false);
  };

  const handleSaveItem = (itemData) => {
    console.log("Item data to save:", itemData);
  };

  useEffect(() => {
    setItemOrder(items);
  }, [items]);

  const handleOpenOverlay = async (item) => {
    startLoading();
    try {
      const { data } = await getItemById(item.id);
      setSelectedItem(data);
      setShowOverlay(true);
      setIsAnimating(true);
    } catch (error) {
      console.error(error);
    } finally {
      stopLoading();
    }
  };

  const handleCloseOverlay = () => {
    setShowOverlay(false);
    setTimeout(() => {
      setIsAnimating(false);
      setSelectedItem(null);
    }, 300);
  };

  const moveItem = (dragIndex, hoverIndex) => {
    const updatedItems = [...itemOrder];
    const [draggedItem] = updatedItems.splice(dragIndex, 1);
    updatedItems.splice(hoverIndex, 0, draggedItem);
    setItemOrder(updatedItems);
  };

  const handleDrop = async (item) => {
    if (item.originalIndex !== item.index) {
      const reorderedItems = {
        category_id: selectedCategory.id,
        items: itemOrder.map((item, index) => ({
          id: item.id,
          position: index + 1,
        })),
      };

      try {
        await reorderData(reorderedItems);
      } catch (error) {
        console.error("Reordering failed:", error);
        setItemOrder(items); // Reset to original order on failure
      }
    }
  };

  const correctDim = (
    padding = dimensions.itemListBoxPadding,
    screenSize = "md"
  ) => {
    const itemListWidth = `calc(100svw - ${padding * 2}px)`;
    const itemContainerWidth = `${
      dimensions.contentContainerWidth - padding
    }px`;
    let itemListHeight = "";
    if (screenSize === "md") {
      itemListHeight = 100 - dimensions.upperContentHeight + "svh";
    } else if (screenSize === "xs") {
      itemListHeight = `45svh`;
    }
    return { itemListWidth, itemContainerWidth, itemListHeight };
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: { xs: correctDim().itemListWidth, md: "100svw" },
          height: {
            xs: correctDim("1", "xs").itemListHeight,
            md: correctDim("1", "md").itemListHeight,
          },
          backgroundColor: theme.palette.secondary.main,
          backgroundImage: isImageUrl(themeBg) ? `url(${themeBg})` : "none",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          px: { xs: `${dimensions.itemListBoxPadding}px`, md: 0 },
          zIndex: 40,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { md: correctDim().itemContainerWidth },
          }}
        >
          <Box>
            {isAnimating && (
              <ItemDetails
                selectedCategory={selectedCategory}
                selectedItem={selectedItem}
                handleCloseOverlay={handleCloseOverlay}
                height={dimensions.upperContentHeight}
                isOpen={showOverlay}
                handleOpenItemForm={handleOpenItemForm}
              />
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                height: "35px",
              }}
            >
              <SelectedCategoryName selectedCategory={selectedCategory} />
            </Box>
            <Items
              items={itemOrder}
              handleOpenOverlay={handleOpenOverlay}
              moveItem={moveItem}
              handleDrop={handleDrop}
              editingItemId={editingItemId}
              handleCloseItemForm={handleCloseItemForm}
              handleOpenItemForm={handleOpenItemForm}
              handleSaveItem={handleSaveItem}
              isItemFormOpen={isItemFormOpen}
            />
            <ItemForm
              onClose={handleCloseItemForm}
              onSave={handleSaveItem}
              open={isItemFormOpen}
              itemId={editingItemId}
            />
            <ReminderDialog
              open={isReminderOpen}
              onClose={handleCloseItemFormAfterReminderYes}
              onCloseCancel={handleCloseItemFormAfterReminderCancel}
            />
          </Box>
        </Box>
      </Box>
    </DndProvider>
  );
};

const ReminderDialog = ({ open, onClose, onCloseCancel }) => {
  return (
    <Dialog open={open} onClose={onCloseCancel}>
      <DialogTitle>
        <CloseDialogButton onClose={onCloseCancel} />
      </DialogTitle>

      <DialogContent>
        <Box sx={{ paddingInline: "16px", paddingBlock: "20px" }}>
          <Typography variant="h4" color="primary">
            Reminder
          </Typography>
          <Typography variant="h6" my={2}>
            The item is in-active, Continue?
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
            <Button onClick={onClose}>Yes</Button>
            <Button onClick={onCloseCancel}>Cancel</Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ItemListAdmin;

const SelectedCategoryName = ({ selectedCategory }) => {
  const theme = useTheme();
  const { selectedLanguage } = useAuth();

  const translatedName =
    selectedCategory?.languages?.find(
      (lang) => lang.language_id === selectedLanguage.language_id
    )?.name || selectedCategory?.name;
  return (
    <Typography variant="h4" sx={{ color: theme.palette.primary.main }}>
      {translatedName}
    </Typography>
  );
};

const Items = ({
  items,
  handleOpenOverlay,
  moveItem,
  handleDrop,
  handleOpenItemForm,
}) => {
  const theme = useTheme();
  const isLoading = items.length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        gap: "16px",
        overflowX: "auto",
        overflowY: "visible",
        py: "5px",
        flexGrow: 1,
        "&::-webkit-scrollbar": {
          height: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.primary.main,
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.secondary.main,
        },
      }}
    >
      <SharpOutlinedButton onClick={() => handleOpenItemForm()} />

      {isLoading ? (
        <ItemSkeleton />
      ) : (
        items?.map((item, index) => (
          <DraggableItem
            key={item.id}
            item={item}
            index={index}
            handleOpenOverlay={handleOpenOverlay}
            moveItem={moveItem}
            handleDrop={handleDrop}
            handleOpenItemForm={handleOpenItemForm}
          />
        ))
      )}
    </Box>
  );
};

const DraggableItem = ({
  item,
  index,
  handleOpenOverlay,
  moveItem,
  handleDrop,
  handleOpenItemForm,
}) => {
  const { selectedLanguage } = useAuth();
  const theme = useTheme();
  const ref = useRef(null);

  const translatedName =
    item.languages?.find(
      (lang) => lang.language_id === selectedLanguage.language_id
    )?.name || item.name;

  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.ITEM,
    hover(item, monitor) {
      if (!ref.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleX =
        (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientX = clientOffset.x - hoverBoundingRect.left;

      if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
        return;
      }

      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ITEM,
    item: {
      id: item.id,
      index,
      originalIndex: index,
    },
    end: (item, monitor) => {
      const didDrop = monitor.didDrop();
      if (!didDrop) {
        moveItem(item.index, item.originalIndex);
      } else {
        handleDrop(item);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <Box
      ref={ref}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        position: "relative",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
      onClick={() => handleOpenOverlay(item)}
    >
      {item.new === 1 && (
        <div
          style={{
            position: "absolute",
            top: "-63px",
            zIndex: "1",
          }}
        >
          <ItemLabel
            title="New"
            style={{
              color: theme.palette.primary.main,
              transform: "scale(0.3)",
            }}
          />
        </div>
      )}
      {item.top === 1 && (
        <div
          style={{
            position: "absolute",
            top: "-63px",
            zIndex: "1",
          }}
        >
          <ItemLabel
            title="Top"
            style={{
              color: theme.palette.primary.main,
              transform: "scale(0.3)",
            }}
          />
        </div>
      )}
      <EditButton onClick={() => handleOpenItemForm(item.id)} />
      <Box
        sx={{
          width: { xs: "140px", md: "114px" },
          height: { xs: "140px", md: "114px" },
          borderRadius: "50%",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `3px solid ${theme.palette.tertiary.main}`,
          backgroundColor: theme.palette.primary.main,
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          cursor: "pointer",
        }}
      >
        <LazyImage
          src={item.image}
          alt={translatedName}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            backgroundColor: "#cccccc",
            opacity: !item.active ? 0.5 : "unset",
          }}
        />
        {!item.active && (
          <Box
            sx={{
              position: "absolute",
              top: "55%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#000",
              color: "#fff",
              padding: "2px 8px 4px 8px",
              borderRadius: "12px",
              fontSize: "12px",
            }}
          >
            Archived
          </Box>
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: "3",
          width: "114px",
          marginTop: "8px",
          fontSize: { xs: "14px", md: "12px" },
        }}
      >
        {translatedName}
      </Typography>
    </Box>
  );
};

function SharpOutlinedButton({ onClick }) {
  return (
    <Box
      sx={{
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Button
        variant="outlined"
        sx={{
          border: "2px solid",
          width: { xs: "140px", md: "118px" },
          height: { xs: "140px", md: "118px" },
          borderRadius: "50%",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={onClick}
      >
        <AddIcon />
      </Button>
      <Typography
        variant="body2"
        sx={{
          width: "114px",
          marginTop: "8px",
          fontSize: { xs: "14px", md: "12px" },
        }}
      >
        Add item
      </Typography>
    </Box>
  );
}
